import { validComparisonDimension, validId, validLocalDate, validRevision, trainingFailure } from './training.ts';
import type { ComparisonDimension, TrainingResult, WorkingSet, SessionStatus } from './training.ts';

export type ProgressResult<T> = TrainingResult<T>;
export const failure = trainingFailure;
export interface Period { startLocalDate: string; endLocalDate: string }
export interface WeightObservation {
  observationId: string;
  localDate: string;
  recordedInstant: string;
  value: number;
  unit: 'kg';
  revision: number;
}
export const validPeriod = (p: Period): boolean => !!p && validLocalDate(p.startLocalDate)
  && validLocalDate(p.endLocalDate) && p.startLocalDate <= p.endLocalDate;
export const inPeriod = (date: string, p: Period) => date >= p.startLocalDate && date <= p.endLocalDate;
export function validWeight(v: unknown): v is WeightObservation {
  const w = v as WeightObservation;
  return !!w && validId(w.observationId) && validLocalDate(w.localDate)
    && typeof w.recordedInstant === 'string'
    && /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(w.recordedInstant)
    && validLocalDate(w.recordedInstant.slice(0,10))
    && Number.isFinite(Date.parse(w.recordedInstant)) && Number.isFinite(w.value)
    && w.value > 0 && w.unit === 'kg' && validRevision(w.revision);
}
export function correctWeight(current: WeightObservation, candidate: WeightObservation, expected: number): ProgressResult<WeightObservation> {
  if (!validWeight(current) || !validWeight(candidate) || !validRevision(expected)) return failure('VALIDATION_ERROR');
  if (current.revision !== expected) return failure('REVISION_CONFLICT');
  if (current.observationId !== candidate.observationId || !Number.isSafeInteger(current.revision + 1)
    || candidate.revision !== current.revision + 1) return failure('INVARIANT_CONFLICT');
  return { ok: true, value: { ...candidate } };
}
export function parseWeight(raw: string): ProgressResult<number> {
  if (typeof raw !== 'string' || !/^\d+(?:[.,]\d+)?$/.test(raw.trim())) return failure('VALIDATION_ERROR');
  const value = Number(raw.trim().replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? { ok: true, value } : failure('VALIDATION_ERROR');
}
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
// Both inputs have already passed validWeight. Date.parse establishes the UTC
// second; compare the original fractional digits without truncating to ms.
export function compareWeightInstants(a: string, b: string): number {
  const second = Date.parse(a.replace(/\.\d+/,'')) - Date.parse(b.replace(/\.\d+/,''));
  if (second !== 0) return Math.sign(second);
  const af = /\.(\d+)/.exec(a)?.[1] ?? '', bf = /\.(\d+)/.exec(b)?.[1] ?? '';
  const width = Math.max(af.length,bf.length), left=af.padEnd(width,'0'), right=bf.padEnd(width,'0');
  return left === right ? 0 : left < right ? -1 : 1;
}

// Decimal rational arithmetic preserves exact input decimals through averages
// and subtraction. Rounding belongs only to presentation, including negatives.
interface Fraction { n: bigint; d: bigint }
function decimal(value: number): Fraction {
  const [mantissa, exponent = '0'] = value.toString().toLowerCase().split('e');
  const parts = mantissa!.split('.');
  const scale = (parts[1]?.length ?? 0) - Number(exponent);
  const n = BigInt(parts.join(''));
  return scale >= 0 ? { n, d: 10n ** BigInt(scale) } : { n: n * 10n ** BigInt(-scale), d: 1n };
}
const add = (a: Fraction, b: Fraction): Fraction => ({ n: a.n * b.d + b.n * a.d, d: a.d * b.d });
function numeric(x: Fraction): number {
  if (x.n === 0n) return 0;
  const n = x.n < 0n ? -x.n : x.n;
  // Convert the quotient, never huge numerator/denominator separately. Keep
  // more significant decimal digits than a binary Number can represent.
  const scale = 20 - (n.toString().length - x.d.toString().length);
  const digits = scale >= 0 ? n * 10n ** BigInt(scale) / x.d : n / (x.d * 10n ** BigInt(-scale));
  return Number(`${x.n < 0n ? '-' : ''}${digits}e${-scale}`);
}
function display(x: Fraction, signed = false): string {
  const absolute = x.n < 0n ? -x.n : x.n;
  const tenths = (absolute * 20n + x.d) / (2n * x.d);
  return `${tenths === 0n ? '' : x.n < 0n ? '-' : signed ? '+' : ''}${tenths / 10n}.${tenths % 10n}`;
}
export const displayWeight = (value: number, signed = false) => display(decimal(value), signed);
export interface WeightWindow extends Period { count: number; coverage: number; mean?: number; display?: string }
function representatives(weights: readonly WeightObservation[], period: Period): ProgressResult<WeightObservation[]> {
  const identities = new Set<string>(), days = new Map<string, WeightObservation>();
  for (const w of weights) {
    if (!validWeight(w) || identities.has(w.observationId)) return failure('CORRUPTION_DETECTED');
    identities.add(w.observationId);
    if (!inPeriod(w.localDate, period)) continue;
    const old = days.get(w.localDate), comparison = old ? compareWeightInstants(w.recordedInstant,old.recordedInstant) : -1;
    if (!old || comparison < 0 || (comparison === 0 && w.observationId < old.observationId)) days.set(w.localDate, w);
  }
  return { ok: true, value: [...days.values()] };
}
function window(weights: readonly WeightObservation[], end: string): ProgressResult<{ view: WeightWindow; exact?: Fraction }> {
  const period = { startLocalDate: shiftDate(end, -6), endLocalDate: end };
  const selected = representatives(weights, period); if (!selected.ok) return selected;
  const count = selected.value.length;
  const view: WeightWindow = { ...period, count, coverage: count / 7 };
  if (count < 4) return { ok: true, value: { view } };
  const sum = selected.value.reduce((s, w) => add(s, decimal(w.value)), { n: 0n, d: 1n });
  const exact = { n: sum.n, d: sum.d * BigInt(count) };
  return { ok: true, value: { view: { ...view, mean: numeric(exact), display: display(exact) }, exact } };
}
export function weightMetrics(weights: readonly WeightObservation[], date: string) {
  if (!validLocalDate(date) || date < '0100-01-14') return failure('VALIDATION_ERROR');
  const recent = window(weights, date); if (!recent.ok) return recent;
  const previous = window(weights, shiftDate(date, -7)); if (!previous.ok) return previous;
  const a = recent.value.exact, b = previous.value.exact;
  const delta = a && b ? { n: a.n * b.d - b.n * a.d, d: a.d * b.d } : undefined;
  return { ok: true as const, value: { recent: recent.value.view, previous: previous.value.view,
    ...(delta ? { difference: numeric(delta), displayDifference: display(delta, true) } : {}) } };
}

export interface TrainingAdherenceFact { sessionId: string; cohortLocalDate: string; status: SessionStatus }
export interface NutritionAdherenceFact { plannedMealId: string; localDate: string; status: 'REALIZADA' | 'OMITIDA' | 'SIN_REGISTRAR' }
function ratios(completed: number, resolved: number, total: number) {
  return { total, coverage: total ? resolved / total : null, plannedRatio: total ? completed / total : null,
    resolvedRatio: resolved ? completed / resolved : null };
}
export function trainingAdherence(facts: readonly TrainingAdherenceFact[], period: Period) {
  if (!validPeriod(period)) return failure('VALIDATION_ERROR');
  let C = 0, I = 0, O = 0, U = 0;
  for (const f of facts.filter(f => inPeriod(f.cohortLocalDate, period))) {
    if (f.status === 'COMPLETADA') C++; else if (f.status === 'INCOMPLETA') I++;
    else if (f.status === 'OMITIDA') O++; else U++;
  }
  return { ok: true as const, value: { C, I, O, U, ...ratios(C, C + I + O, C + I + O + U) } };
}
export function nutritionAdherence(facts: readonly NutritionAdherenceFact[], improvisedDates: readonly string[], period: Period) {
  if (!validPeriod(period)) return failure('VALIDATION_ERROR');
  let R = 0, O = 0, U = 0;
  for (const f of facts.filter(f => inPeriod(f.localDate, period))) {
    if (f.status === 'REALIZADA') R++; else if (f.status === 'OMITIDA') O++; else U++;
  }
  return { ok: true as const, value: { R, O, U, ...ratios(R, R + O, R + O + U),
    improvised: improvisedDates.filter(date => inPeriod(date, period)).length } };
}
export interface PerformanceContext {
  exerciseId: string;
  variant: ComparisonDimension;
  equipment: ComparisonDimension;
  configuration: ComparisonDimension;
}

export interface PerformanceFact {
  set: WorkingSet;
  name: string;
  startedInstant: string;
  context: PerformanceContext;
}

function comparableDimension(
  a: ComparisonDimension,
  b: ComparisonDimension,
): boolean {
  if (!validComparisonDimension(a) || !validComparisonDimension(b)) {
    return false;
  }

  if (a.state === 'UNKNOWN' || b.state === 'UNKNOWN') {
    return false;
  }

  if (a.state === 'NOT_APPLICABLE'
    || b.state === 'NOT_APPLICABLE') {
    return a.state === 'NOT_APPLICABLE'
      && b.state === 'NOT_APPLICABLE';
  }

  return a.value.trim() === b.value.trim();
}

export function comparable(
  a: PerformanceFact,
  b: PerformanceFact,
): boolean {
  if (!validId(a.context.exerciseId)
    || a.context.exerciseId !== b.context.exerciseId
    || !comparableDimension(a.context.variant, b.context.variant)
    || !comparableDimension(a.context.equipment, b.context.equipment)
    || !comparableDimension(a.context.configuration, b.context.configuration)
    || a.set.loadKind !== b.set.loadKind) {
    return false;
  }

  if (a.set.loadKind === 'PESO_CORPORAL') {
    return a.set.load === undefined
      && b.set.load === undefined;
  }

  return !!a.set.load?.unit
    && a.set.load.unit === b.set.load?.unit;
}
